<script setup lang="ts">
import { getCurrentInstance, type PropType } from 'vue'
import { useInputHelpers } from '../../composables/useInputHelpers'
import OenUiInputValidation from '../OenUiInputValidation/OenUiInputValidation.vue'
import type { ValidationValue } from '../OenUiInputValidation/types'

const props = defineProps({
  componentValidation: {
    type: Object as PropType<ValidationValue<unknown>>,
    required: false,
    default: () => ({}),
  },
  componentLabel: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
  hideLabel: {
    type: Boolean,
    required: false,
    default: false,
  },
  inlineLabel: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const { parent } = getCurrentInstance()
const {
  root,
  wrapper,
  label,
  input,
  validationHelpers,
  props: parentProps,
} = useInputHelpers(parent, props)

defineExpose({
  input,
  label,
})

defineOptions({
  name: 'OenUiInputWrapper',
})
</script>

<template>
  <div :class="root.classes">
    <!--eslint-disable-next-line vuejs-accessibility/label-has-for-->
    <label v-if="label.text && !hideLabel" :id="label.id" :class="label.classes" :for="input.id">
      {{ label.text }}<slot :class="label.classes" name="labelRight"></slot>
    </label>
    <div :class="wrapper.classes">
      <!-- @slot Place an input element inside. provides slot props for bindings
            @binding: {object} root the root element bindings
            @binding: {object} wrapper the wrapper element bindings
            @binding: {object} label the label element bindings
            @binding: {object} input the input element bindings
            @binding: {object} validation the input element bindings
            @binding: {object} validation-helpers the input element bindings
            -->
      <slot
        :root="root"
        :wrapper="wrapper"
        :label="label"
        :input="input"
        :validation="wrapper.validation"
        :validation-helpers="validationHelpers"
      />
    </div>
    <OenUiInputValidation
      v-if="wrapper.validation || parentProps?.helperText"
      :validation="wrapper.validation"
      :validation-helpers="validationHelpers"
      :helper-text="parentProps?.helperText"
    />
  </div>
</template>
<!--Debug-->
<!--<div>-->
<!--  <pre>{{ root }}</pre>-->
<!--  <pre>{{ wrapper }}</pre>-->
<!--  <pre>{{ label }}</pre>-->
<!--  <pre>{{ input }}</pre>-->
<!--</div>-->

<style scoped>
.oen-ui-input {
  --oen-ui-input-label-color: inherit;
  --oen-ui-input-input-text-color: inherit;
  --oen-ui-input-input-border-color: theme('colors.gray.light');
  --oen-ui-input-input-border-color-focus: theme('colors.oen.ui.primary.DEFAULT');
  --oen-ui-input-input-caret-color: theme('colors.oen.ui.primary.DEFAULT');
  --oen-ui-input-input-placeholder-color: theme('colors.tertiary.text');
  --oen-ui-input-input-icon-color: theme('colors.secondary.text');
  --oen-ui-input-helper-color: theme('colors.secondary.text');
  @apply mb-6;

  :deep(.p-component) {
    @apply font-sans;
  }

  &.oen-ui-input--inline {
    @apply grid items-baseline;
    grid-template-columns: max-content auto;
    grid-template-rows: max-content auto;

    :deep(.oen-ui-input-label) {
      @apply me-4 row-span-full;
    }
  }

  :deep(.oen-ui-input-input) {
    @apply block w-full h-12 px-3 relative;
    @apply border rounded-lg;
    @apply outline-none;
    @apply type-subtitle;
    @apply border-[var(--oen-ui-input-input-border-color)];
    @apply caret-[var(--oen-ui-input-input-caret-color)];
    @apply text-[var(--oen-ui-input-input-text-color)];
    &:hover {
      @apply border-[var(--oen-ui-input-input-border-color)];
    }
    &:focus,
    > input:focus {
      --oen-ui-input-input-border-color: theme('colors.oen.ui.primary.DEFAULT');
      @apply shadow-outline-input-focus;
      @apply shadow-[var(--oen-ui-input-input-border-color-focus)];
      @apply outline-none;
      @apply border-[var(--oen-ui-input-input-border-color)];
    }
    &::placeholder {
      @apply text-[var(--oen-ui-input-input-placeholder-color)];
    }
    /*Make Prime child inputs occupy space and mimic styles */
    &.p-inputwrapper {
      @apply w-full;
      > .p-inputtext {
        @apply w-full;
        @apply outline-none;
        @apply text-[var(--oen-ui-input-input-text-color)];
      }
    }
  }
  &.oen-ui-input--error,
  &.oen-ui-input--error :deep(.oen-ui-input-input) {
    --oen-ui-input-input-border-color: theme('colors.red.DEFAULT');
    --oen-ui-input-input-border-color-focus: theme('colors.red.DEFAULT');
  }

  &.oen-ui-input--disabled:not(.oen-ui-input--readonly) {
    --oen-ui-input-input-text-color: theme('colors.tertiary.text');
  }
  &.oen-ui-input--disabled,
  &.oen-ui-input--readonly,
  &.oen-ui-input--readonly :deep(.oen-ui-input-input:focus) {
    --oen-ui-input-label-color: theme('colors.tertiary.text');
    --oen-ui-input-helper-color: theme('colors.gray.light');
    --oen-ui-input-input-icon-color: theme('colors.gray.light');
    --oen-ui-input-input-border-color: theme('colors.gray.light');

    :deep(.p-autocomplete-input),
    :deep(.p-inputtext),
    :deep(.p-component) {
      @apply bg-transparent;
    }
  }

  &.oen-ui-input--disabled :deep(.oen-ui-input-input[disabled]),
  &.oen-ui-input--readonly :deep(.oen-ui-input-input[readonly]) {
    @apply bg-white;
    @apply opacity-100;
  }

  &.oen-ui-input--with-left-icon :deep(input) {
    /* Whilst in theory, uses tailwind sizes, we are using absolute for accessibility: 12 + 24 + 8*/
    padding-inline-start: 44px;
  }
  &.oen-ui-input--with-right-icon :deep(input) {
    /* Whilst in theory, uses tailwind sizes, we are using absolute for accessibility: 12 + 24 + 8*/
    padding-inline-end: 44px;
  }

  :deep(.oen-ui-input-wrapper) {
    @apply mt-px relative;
  }
  :deep(.oen-ui-input-label) {
    @apply type-subtitle font-bold;
    @apply mb-1;
    @apply text-[var(--oen-ui-input-label-color)];
  }

  :deep(.oen-ui-input-left-icon),
  :deep(.oen-ui-input-right-icon) {
    @apply absolute inset-y-0 flex items-center;
    @apply text-[var(--oen-ui-input-input-icon-color)];
    button {
      @apply outline-none h-6;
    }

    /* special case for button icon */
    .oen-ui-button.oen-ui-button--icon,
    .oen-ui-button.oen-ui-button--icon .oen-ui-button-inner {
      width: 36px;
      height: 36px;
    }
  }
  :deep(.oen-ui-input-left-icon) {
    @apply start-0;
    @apply ps-3;
    @apply pe-2;

    /* special case for button icon - offset padding left pl-3 */
    .oen-ui-button--icon {
      @apply -ms-1_5;
    }
  }

  :deep(.oen-ui-input-right-icon) {
    @apply end-0;
    @apply pe-3;
    @apply ps-2;

    /* special case for button icon - offset padding right pr-3 */
    .oen-ui-button--icon {
      @apply -me-1_5;
    }
  }

  :deep(.oen-ui-input-validation),
  :deep(.oen-ui-input-helper) {
    @apply mt-1 type-helper;
    @apply text-[var(--oen-ui-input-helper-color)];
  }
  :deep(.oen-ui-input-validation) {
    --oen-ui-input-helper-color: theme('colors.red.DEFAULT');
  }
  :deep(.oen-ui-input-helper) {
    @apply type-helper;
  }
}
</style>
